<template>
  <div class="card mt-2 mt-md-4 gasto-list-card">
    <div class="card-body p-2 p-md-3">
      <div class="d-flex flex-column flex-md-row flex-wrap justify-content-between align-items-stretch align-items-md-center gap-2 mb-3">
        <div class="d-flex flex-wrap gap-2 align-items-center">
          <button class="btn btn-marca btn-sm d-none d-md-inline-flex" @click="$emit('nuevo-gasto')">
            <i class="bi bi-plus-lg me-1"></i> Nuevo gasto
          </button>
          <div class="btn-group flex-grow-1 flex-md-grow-0">
            <button
              class="btn btn-outline-secondary btn-sm"
              @click="ordenarPor('fecha')"
            >
              Por Fecha
            </button>
            <button
              class="btn btn-outline-secondary btn-sm"
              @click="ordenarPor('cantidad')"
            >
              Por Cantidad
            </button>
          </div>
        </div>
      </div>
      <div class="input-group mb-2">
        <span class="input-group-text bg-white">
          <i class="bi bi-search"></i>
        </span>
        <input
          type="text"
          class="form-control"
          placeholder="Buscar por concepto..."
          v-model="busqueda"
        />
        <button
          v-if="busqueda"
          class="btn btn-outline-secondary"
          type="button"
          @click="busqueda = ''"
          title="Limpiar búsqueda"
        >
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
      <div class="mb-2">
        <button type="button" class="btn btn-link btn-sm gastos-filtros-toggle px-0" @click="filtrosAbiertos = !filtrosAbiertos">
          <i class="bi" :class="filtrosAbiertos ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
          Filtros
          <span v-if="fechaDesde || fechaHasta || categoriaFiltro" class="gastos-filtro-punto" aria-hidden="true"></span>
        </button>
      </div>
      <div v-show="filtrosAbiertos" class="row g-2 mb-2 align-items-end">
        <div class="col-12 col-sm-6 col-md-3">
          <label class="form-label small text-muted mb-0" for="gasto-fecha-inicio">
            <i class="bi bi-calendar-event me-1"></i>Fecha inicio
          </label>
          <input
            id="gasto-fecha-inicio"
            type="date"
            class="form-control form-control-sm gasto-fecha-filtro"
            v-model="fechaDesde"
            :max="fechaHasta || undefined"
            title="Fecha inicio del rango (inclusive)"
          />
        </div>
        <div class="col-12 col-sm-6 col-md-3">
          <label class="form-label small text-muted mb-0" for="gasto-fecha-fin">
            <i class="bi bi-calendar-check me-1"></i>Fecha fin
          </label>
          <input
            id="gasto-fecha-fin"
            type="date"
            class="form-control form-control-sm gasto-fecha-filtro"
            v-model="fechaHasta"
            :min="fechaDesde || undefined"
            title="Fecha fin del rango (inclusive)"
          />
        </div>
        <div class="col-12 col-md-4">
          <label class="form-label small text-muted mb-0">Categoría</label>
          <select class="form-select form-select-sm" v-model="categoriaFiltro">
            <option value="">Todas</option>
            <option v-for="cat in categorias" :key="cat.id" :value="String(cat.id)">
              {{ cat.nombre }}
            </option>
          </select>
        </div>
        <div class="col-12 col-md-2">
          <button
            v-if="hayFiltrosActivos"
            type="button"
            class="btn btn-outline-secondary btn-sm w-100"
            @click="limpiarFiltros"
          >
            Limpiar filtros
          </button>
        </div>
      </div>
      <div v-show="filtrosAbiertos" class="mb-2">
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm gasto-ultimo-mes-btn"
          title="Desde el mismo día del mes calendario anterior hasta ayer (sin incluir hoy). Ordena por importe de mayor a menor."
          @click="aplicarFiltroUltimoMes"
        >
          <i class="bi bi-calendar-range me-1"></i>Último mes
        </button>
      </div>
      <div v-if="hayFiltrosActivos && gastosFiltrados.length === 0" class="text-center text-muted py-3">
        <i class="bi bi-funnel me-1"></i> No hay gastos que coincidan con los filtros seleccionados.
      </div>
      <!-- Vista móvil: tarjetas (pulsar en cualquier sitio abre edición) -->
      <div class="d-md-none gasto-cards">
        <div
          v-for="gasto in gastosPaginados"
          :key="gasto.id"
          class="card mb-2 border gasto-card-mobile"
          @click="startEdit(gasto)"
        >
          <div class="card-body py-2 px-3">
            <div class="d-flex gap-2">
              <div class="flex-grow-1 min-w-0 overflow-hidden">
                <div class="fw-bold text-truncate" :title="gasto.concepto">
                  {{ gasto.concepto }}
                  <span v-if="esSuscripcionGastoUi(gasto)" class="gasto-pill">Suscripción</span>
                </div>
                <span class="badge bg-light text-dark border d-inline-block mt-1 text-truncate categoria-badge" :title="gasto.categoria_nombre">{{ gasto.categoria_nombre }}</span>
                <div class="mt-1">
                  <span class="fw-semibold cifra-gasto">{{ formatCantidad(gasto.cantidad_total) }}€</span>
                  <small v-if="gasto.tipo_division === 'mitad'" class="text-muted ms-1">
                    ({{ formatCantidad(gasto.cantidad) }}€ ÷2)
                  </small>
                  <small v-else-if="gasto.tipo_division === 'grupo'" class="text-muted ms-1">
                    ({{ formatCantidad(gasto.cantidad) }}€ ÷{{ gasto.num_participantes || 2 }})
                  </small>
                </div>
              </div>
              <div class="d-flex flex-column align-items-end gap-1 flex-shrink-0">
                <span class="badge bg-secondary" style="font-size: 0.7rem;">
                  <i class="bi bi-calendar3 me-1"></i>{{ formatDate(gasto.fecha) }}
                </span>
                <button
                  class="btn btn-sm btn-danger p-1"
                  @click.stop="deleteGasto(gasto.id)"
                  title="Eliminar"
                  aria-label="Eliminar gasto"
                >
                  <i class="bi bi-trash"></i>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <!-- Vista escritorio: tabla -->
      <div class="d-none d-md-block table-responsive">
        <table class="table table-hover table-sm gastos-clasificados">
          <thead>
            <tr>
              <th @click="ordenarPor('fecha')">
                Fecha
                <i class="bi" :class="getIconoOrdenamiento('fecha')"></i>
              </th>
              <th @click="ordenarPor('concepto')">
                Concepto
                <i class="bi" :class="getIconoOrdenamiento('concepto')"></i>
              </th>
              <th>Categoría</th>
              <th @click="ordenarPor('cantidad')">
                Importe
                <i class="bi" :class="getIconoOrdenamiento('cantidad')"></i>
              </th>
              <th>Acciones</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="gasto in gastosPaginados"
              :key="gasto.id"
            >
              <td>{{ formatDate(gasto.fecha) }}</td>
              <td>
                {{ gasto.concepto }}
                <span v-if="esSuscripcionGastoUi(gasto)" class="gasto-pill">Suscripción</span>
              </td>
              <td>{{ gasto.categoria_nombre }}</td>
              <td>
                <span class="cifra-gasto">{{ formatCantidad(gasto.cantidad_total) }}€</span>
                <small v-if="gasto.tipo_division === 'mitad'" class="text-muted ms-1">
                  ({{ formatCantidad(gasto.cantidad) }}€ ÷2)
                </small>
                <small v-else-if="gasto.tipo_division === 'grupo'" class="text-muted ms-1">
                  ({{ formatCantidad(gasto.cantidad) }}€ ÷{{ gasto.num_participantes || 2 }})
                </small>
              </td>
              <td>
                <button class="btn btn-sm btn-warning me-1" @click="startEdit(gasto)" title="Editar">
                  <i class="bi bi-pencil"></i>
                </button>
                <button class="btn btn-sm btn-danger" @click="deleteGasto(gasto.id)" title="Eliminar">
                  <i class="bi bi-trash"></i>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <!-- Paginación -->
      <nav v-if="pagination.totalPages > 1" class="mt-3 pagination-nav">
        <ul class="pagination justify-content-center pagination-sm flex-wrap">
          <li class="page-item" :class="{ disabled: pagination.currentPage === 1 }">
            <a class="page-link" href="#" @click.prevent="cambiarPagina(pagination.currentPage - 1)">
              Anterior
            </a>
          </li>
          
          <!-- Primera página -->
          <li v-if="primeraPaginaVisible > 1" class="page-item">
            <a class="page-link" href="#" @click.prevent="cambiarPagina(1)">1</a>
          </li>
          
          <!-- Elipsis inicial -->
          <li v-if="primeraPaginaVisible > 2" class="page-item disabled">
            <span class="page-link">...</span>
          </li>
          
          <!-- Páginas visibles -->
          <li 
            v-for="pagina in paginasVisibles" 
            :key="pagina"
            class="page-item"
            :class="{ active: pagination.currentPage === pagina }"
          >
            <a class="page-link" href="#" @click.prevent="cambiarPagina(pagina)">
              {{ pagina }}
            </a>
          </li>
          
          <!-- Elipsis final -->
          <li v-if="ultimaPaginaVisible < pagination.totalPages - 1" class="page-item disabled">
            <span class="page-link">...</span>
          </li>
          
          <!-- Última página -->
          <li v-if="ultimaPaginaVisible < pagination.totalPages" class="page-item">
            <a class="page-link" href="#" @click.prevent="cambiarPagina(pagination.totalPages)">
              {{ pagination.totalPages }}
            </a>
          </li>
          
          <li class="page-item" :class="{ disabled: pagination.currentPage === pagination.totalPages }">
            <a class="page-link" href="#" @click.prevent="cambiarPagina(pagination.currentPage + 1)">
              Siguiente
            </a>
          </li>
        </ul>
      </nav>
    </div>
  </div>

  <!-- Modal de Edición -->
  <div class="modal fade" id="editModal" tabindex="-1" aria-labelledby="editModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-dialog-centered modal-fullscreen-sm-down">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="editModalLabel">Editar Gasto</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body">
          <form @submit.prevent="saveEdit">
            <div class="mb-3">
              <label for="editFecha" class="form-label">Fecha</label>
              <input 
                type="date" 
                class="form-control" 
                id="editFecha" 
                v-model="editForm.fecha" 
                required
              >
            </div>
            <div class="mb-3">
              <label for="editConcepto" class="form-label">Concepto</label>
              <input 
                type="text" 
                class="form-control" 
                id="editConcepto" 
                v-model="editForm.concepto" 
                required
              >
            </div>
            <div class="mb-3">
              <label for="editCategoria" class="form-label">Categoría</label>
              <select class="form-select" id="editCategoria" v-model="editForm.categoria_id" required>
                <option value="">Selecciona una categoría</option>
                <option v-for="cat in categorias" :key="cat.id" :value="cat.id">
                  {{ cat.nombre }}
                </option>
              </select>
            </div>
            <div class="mb-3">
              <label for="editCantidad" class="form-label">Cantidad</label>
              <input 
                type="number" 
                step="0.01" 
                class="form-control" 
                id="editCantidad" 
                v-model="editForm.cantidad" 
                required
              >
            </div>
            <div class="mb-3" v-if="editForm.tipo_division !== 'grupo'">
              <label for="editTipoDivision" class="form-label">División</label>
              <select class="form-select" id="editTipoDivision" v-model="editForm.tipo_division">
                <option value="total">Total</option>
                <option value="mitad">Mitad</option>
              </select>
            </div>
            <div class="mb-3" v-else>
              <label class="form-label">División</label>
              <div class="form-control bg-light">
                Grupo · tu parte
                {{ formatCantidad(calcularCantidadTotal()) }} €
                ({{ editForm.num_participantes || 2 }} personas)
              </div>
            </div>
            <div class="mb-3">
              <label for="editClasificacion" class="form-label">Tipo de gasto</label>
              <select class="form-select" id="editClasificacion" v-model="editForm.clasificacion_manual">
                <option value="auto">Automático (script suscripción → periódico; resto → puntual)</option>
                <option value="puntual">Puntual (fijar)</option>
                <option value="suscripcion">Suscripción / periódico (fijar)</option>
              </select>
              <div class="form-text">
                Si eliges «fijar», esa decisión prevalece sobre la automática. El script diario de suscripciones marca esos gastos como periódicos; tú puedes cambiarlos a puntuales aquí.
              </div>
            </div>
            <div class="modal-footer">
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancelar</button>
              <button type="submit" class="btn btn-primary">Guardar Cambios</button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent, ref, onMounted, computed, watch, watchEffect } from 'vue';
import { useStore } from 'vuex';
import axios from '../config/axios';
import { esSuscripcionGastoUi } from '../utils/gastoClasificacion';
import * as bootstrap from 'bootstrap';

function clasificacionToForm(g) {
  const m = g.clasificacion_manual;
  if (m === 0 || m === '0') return 'puntual';
  if (m === 1 || m === '1') return 'suscripcion';
  return 'auto';
}

function formToClasificacion(val) {
  if (val === 'puntual') return 0;
  if (val === 'suscripcion') return 1;
  return null;
}

/** Convierte filtro de fecha (texto) a YYYY-MM-DD. Acepta dd/mm/aaaa o yyyy-mm-dd (p. ej. pegado). */
function parseFechaFiltroAIso(s) {
  if (s == null) return '';
  const t = String(s).trim();
  if (!t) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  const m = t.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return '';
  const d = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const y = parseInt(m[3], 10);
  if (y < 1900 || y > 2100 || mo < 1 || mo > 12 || d < 1 || d > 31) return '';
  const dt = new Date(y, mo - 1, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return '';
  return `${y}-${String(mo).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

/** Valor del datepicker (YYYY-MM-DD) o texto parseable heredado. */
function fechaFiltroAISO(v) {
  const t = v == null ? '' : String(v).trim();
  if (!t) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(t)) return t;
  return parseFechaFiltroAIso(t);
}

/** Fecha local → YYYY-MM-DD (sin desfases UTC). */
function fechaLocalAISO(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

export default defineComponent({
  name: 'GastoList',

  props: {
    gastos: {
      type: Array,
      required: true
    }
  },

  emits: ['gasto-deleted', 'gasto-updated', 'pagina-cambiada', 'nuevo-gasto'],

  setup(props, { emit }) {
    const store = useStore();
    const editingId = ref(null);
    const categorias = ref([]);
    const ordenActual = ref('fecha');
    const ordenAscendente = ref(false);
    const conceptos = ref([]);
    const busqueda = ref('');
    const filtrosAbiertos = ref(false);
    const fechaDesde = ref('');
    const fechaHasta = ref('');
    const categoriaFiltro = ref('');
    const pagination = ref({
      total: 0,
      totalPages: 0,
      currentPage: 1,
      limit: 20,
      perPage: 20
    });

    const editForm = ref({
      fecha: '',
      concepto: '',
      cantidad: '',
      tipo_division: '',
      num_participantes: 1,
      categoria_id: '',
      clasificacion_manual: 'auto'
    });

    const fechaSoloDia = (val) => {
      if (val == null || val === '') return '';
      return String(val).split('T')[0];
    };

    const gastosFiltrados = computed(() => {
      if (!props.gastos) return [];
      let list = props.gastos;

      const termino = busqueda.value.trim().toLowerCase();
      if (termino) {
        list = list.filter(g => g.concepto && g.concepto.toLowerCase().includes(termino));
      }

      const desdeIso = fechaFiltroAISO(fechaDesde.value);
      const hastaIso = fechaFiltroAISO(fechaHasta.value);
      if (desdeIso) {
        list = list.filter(g => fechaSoloDia(g.fecha) >= desdeIso);
      }
      if (hastaIso) {
        list = list.filter(g => fechaSoloDia(g.fecha) <= hastaIso);
      }

      const cat = categoriaFiltro.value;
      if (cat !== '') {
        list = list.filter(g => String(g.categoria_id) === String(cat));
      }

      return list;
    });

    const hayFiltrosActivos = computed(() => {
      return !!(
        busqueda.value.trim() ||
        fechaDesde.value ||
        fechaHasta.value ||
        categoriaFiltro.value
      );
    });

    const limpiarFiltros = () => {
      busqueda.value = '';
      fechaDesde.value = '';
      fechaHasta.value = '';
      categoriaFiltro.value = '';
    };

    /** Mismo día del mes calendario anterior hasta ayer (hoy excluido); orden por importe descendente. */
    const aplicarFiltroUltimoMes = () => {
      const hoy = new Date();
      hoy.setHours(12, 0, 0, 0);
      const ayer = new Date(hoy);
      ayer.setDate(ayer.getDate() - 1);
      const inicio = new Date(hoy);
      inicio.setMonth(inicio.getMonth() - 1);
      fechaDesde.value = fechaLocalAISO(inicio);
      fechaHasta.value = fechaLocalAISO(ayer);
      ordenActual.value = 'cantidad';
      ordenAscendente.value = false;
    };

    const gastosOrdenados = computed(() => {
      if (!gastosFiltrados.value || gastosFiltrados.value.length === 0) return [];

      // Desempate por id desc: dentro del mismo dia / mismo importe / mismo concepto,
      // el gasto creado mas recientemente (id mayor) aparece primero por defecto.
      const tiebreak = (a, b) => (Number(b.id) || 0) - (Number(a.id) || 0);

      return [...gastosFiltrados.value].sort((a, b) => {
        if (ordenActual.value === 'fecha') {
          const fechaA = new Date(a.fecha);
          const fechaB = new Date(b.fecha);
          const diff = ordenAscendente.value ? fechaA - fechaB : fechaB - fechaA;
          return diff !== 0 ? diff : tiebreak(a, b);
        } else if (ordenActual.value === 'cantidad') {
          const diff = ordenAscendente.value
            ? a.cantidad_total - b.cantidad_total
            : b.cantidad_total - a.cantidad_total;
          return diff !== 0 ? diff : tiebreak(a, b);
        } else if (ordenActual.value === 'concepto') {
          const diff = ordenAscendente.value
            ? a.concepto.localeCompare(b.concepto)
            : b.concepto.localeCompare(a.concepto);
          return diff !== 0 ? diff : tiebreak(a, b);
        }
        return tiebreak(a, b);
      });
    });

    // Memoizar los gastos paginados para evitar recálculos innecesarios
    const gastosPaginados = computed(() => {
      const inicio = (pagination.value.currentPage - 1) * pagination.value.limit;
      const fin = inicio + pagination.value.limit;
      return gastosOrdenados.value.slice(inicio, fin);
    });

    const paginasVisibles = computed(() => {
      const paginasAMostrar = 5;
      let inicio = Math.max(1, pagination.value.currentPage - Math.floor(paginasAMostrar / 2));
      let fin = Math.min(pagination.value.totalPages, inicio + paginasAMostrar - 1);

      if (fin === pagination.value.totalPages) {
        inicio = Math.max(1, fin - paginasAMostrar + 1);
      }

      return Array.from({ length: fin - inicio + 1 }, (_, i) => inicio + i);
    });

    const primeraPaginaVisible = computed(() => paginasVisibles.value[0]);
    const ultimaPaginaVisible = computed(() => paginasVisibles.value[paginasVisibles.value.length - 1]);

    const fetchGastos = async (pagina = 1) => {
      try {
        console.log('Fetching gastos para página:', pagina);
        pagination.value.currentPage = pagina;
        
        // Si ya tenemos todos los gastos en memoria, no necesitamos hacer una petición
        // Solo actualizamos la paginación y emitimos el evento
        if (props.gastos && props.gastos.length > 0) {
          console.log('Usando gastos en memoria, total:', props.gastos.length);
          
          pagination.value.total = props.gastos.length;
          pagination.value.totalPages = Math.ceil(props.gastos.length / pagination.value.limit);
          
          console.log('Paginación actualizada:', pagination.value);
          
          // No emitimos evento para no reemplazar los gastos completos
          return;
        }
        
        // Si no tenemos los gastos, hacemos la petición
        const response = await axios.get('/api/gastos', {
          params: {
            page: pagina,
            limit: pagination.value.limit
          }
        });
        
        // Actualizar la paginación con los datos de la respuesta
        if (response.data.pagination) {
          pagination.value = {
            ...pagination.value,
            ...response.data.pagination
          };
        } else if (response.data.meta) {
          pagination.value = {
            ...pagination.value,
            currentPage: response.data.meta.current_page,
            totalPages: response.data.meta.last_page,
            total: response.data.meta.total
          };
        } else {
          // Si no hay información de paginación, asumimos que son todos los gastos
          const gastos = Array.isArray(response.data) ? response.data : 
                        (response.data.gastos || []);
          
          pagination.value.total = gastos.length;
          pagination.value.totalPages = Math.ceil(gastos.length / pagination.value.limit);
        }
        
        console.log('Paginación actualizada desde API:', pagination.value);
        
        // Emitir el evento con los gastos paginados
        emit('pagina-cambiada', response.data.gastos || response.data);
      } catch (error) {
        console.error('Error al obtener gastos:', error);
      }
    };

    const cambiarPagina = async (pagina) => {
      if (pagina >= 1 && pagina <= pagination.value.totalPages) {
        await fetchGastos(pagina);
      }
    };

    const ordenarPor = (campo) => {
      if (ordenActual.value === campo) {
        ordenAscendente.value = !ordenAscendente.value;
      } else {
        ordenActual.value = campo;
        ordenAscendente.value = true;
      }
    };

    const getIconoOrdenamiento = (campo) => {
      if (ordenActual.value !== campo) return 'bi-arrow-down-up';
      return ordenAscendente.value ? 'bi-arrow-up' : 'bi-arrow-down';
    };

    const formatDate = (date) => {
      return new Date(date).toLocaleDateString('es-ES');
    };

    const formatCantidad = (valor) => {
      if (valor === null || valor === undefined) return '0.00';
      return parseFloat(valor).toFixed(2);
    };

    const loadCategorias = async () => {
      try {
        const response = await axios.get('/api/categorias');
        categorias.value = response.data;
      } catch (error) {
        console.error('Error al cargar categorías:', error);
      }
    };

    const loadConceptos = async () => {
      try {
        const response = await axios.get('/api/gastos/conceptos');
        conceptos.value = response.data;
      } catch (error) {
        console.error('Error al cargar conceptos:', error);
      }
    };

    const calcularCantidadTotal = () => {
      if (!editForm.value.cantidad) return 0;
      const cantidadNum = parseFloat(editForm.value.cantidad);
      if (Number.isNaN(cantidadNum)) return 0;
      if (editForm.value.tipo_division === 'grupo') {
        const n = Math.max(1, parseInt(editForm.value.num_participantes, 10) || 1);
        return Math.round((cantidadNum / n) * 100) / 100;
      }
      if (editForm.value.tipo_division === 'mitad') {
        return cantidadNum / 2;
      }
      return cantidadNum;
    };

    const startEdit = (gasto) => {
      editingId.value = gasto.id;
      editForm.value = {
        fecha: gasto.fecha,
        concepto: gasto.concepto,
        cantidad: gasto.cantidad,
        tipo_division: gasto.tipo_division,
        num_participantes: gasto.num_participantes || (gasto.tipo_division === 'mitad' ? 2 : 1),
        categoria_id: gasto.categoria_id,
        clasificacion_manual: clasificacionToForm(gasto)
      };
      // Mostrar el modal de edición
      const editModal = new bootstrap.Modal(document.getElementById('editModal'));
      editModal.show();
    };

    const cancelEdit = () => {
      editingId.value = null;
    };

    const saveEdit = async () => {
      try {
        const { clasificacion_manual, ...rest } = editForm.value;
        const response = await axios.put(`/api/gastos/${editingId.value}`, {
          ...rest,
          cantidad_total: calcularCantidadTotal(),
          clasificacion_manual: formToClasificacion(clasificacion_manual)
        });
        editingId.value = null;
        // Cerrar el modal después de guardar
        const editModal = bootstrap.Modal.getInstance(document.getElementById('editModal'));
        if (editModal) {
          editModal.hide();
        }
        
        console.log('Gasto actualizado correctamente, notificando al componente padre');
        // Emitir evento para que el componente padre recargue los gastos
        emit('gasto-updated');
      } catch (error) {
        console.error('Error al actualizar:', error);
        alert('Error al actualizar el gasto');
      }
    };

    const deleteGasto = async (id) => {
      // Validar que el ID no sea null o undefined
      if (!id) {
        console.error('Error: Intento de eliminar un gasto con ID null o undefined');
        console.error('Gasto que se intenta eliminar:', JSON.stringify(props.gastos.find(g => !g.id), null, 2));
        
        // Mostrar todos los gastos para depuración
        console.error('Lista completa de gastos:', JSON.stringify(props.gastos, null, 2));
        
        alert('Error: No se puede eliminar este gasto porque no tiene un ID válido');
        return;
      }
      
      console.log('Intentando eliminar gasto con ID:', id);
      console.log('Tipo del ID:', typeof id);
      
      if (confirm('¿Estás seguro de eliminar este gasto?')) {
        try {
          console.log('Enviando petición DELETE a /api/gastos/' + id);
          const response = await axios.delete(`/api/gastos/${id}`);
          console.log('Respuesta del servidor:', response.data);
          emit('gasto-deleted', id);
        } catch (error) {
          console.error('Error al eliminar gasto:', error);
          if (error.response) {
            console.error('Detalles del error:', {
              status: error.response.status,
              statusText: error.response.statusText,
              data: error.response.data
            });
          }
          alert('Error al eliminar el gasto');
        }
      }
    };

    watch(
      [busqueda, fechaDesde, fechaHasta, categoriaFiltro],
      () => {
        pagination.value.currentPage = 1;
      }
    );

    watchEffect(() => {
      const total = gastosFiltrados.value.length;
      const limit = pagination.value.limit;
      pagination.value.total = total;
      pagination.value.totalPages = Math.max(1, Math.ceil(total / limit) || 1);
      if (pagination.value.currentPage > pagination.value.totalPages) {
        pagination.value.currentPage = pagination.value.totalPages;
      }
    });

    onMounted(() => {
      loadCategorias();
      loadConceptos();
      if (!props.gastos || props.gastos.length === 0) {
        fetchGastos();
      }
    });

    return {
      gastosOrdenados,
      gastosPaginados,
      gastosFiltrados,
      hayFiltrosActivos,
      limpiarFiltros,
      aplicarFiltroUltimoMes,
      busqueda,
      filtrosAbiertos,
      fechaDesde,
      fechaHasta,
      categoriaFiltro,
      editingId,
      editForm,
      categorias,
      startEdit,
      saveEdit,
      cancelEdit,
      calcularCantidadTotal,
      formatDate,
      formatCantidad,
      deleteGasto,
      ordenarPor,
      getIconoOrdenamiento,
      pagination,
      cambiarPagina,
      paginasVisibles,
      primeraPaginaVisible,
      ultimaPaginaVisible,
      esSuscripcionGastoUi
    };
  }
});
</script>

<style scoped>
.gasto-list-card {
  overflow: hidden;
}

.gastos-filtros-toggle {
  color: var(--marca, #1f6b45);
  font-weight: 600;
  text-decoration: none;
}

.gastos-filtro-punto {
  display: inline-block;
  width: 0.45rem;
  height: 0.45rem;
  margin-left: 0.35rem;
  border-radius: 999px;
  background: var(--marca, #1f6b45);
  vertical-align: middle;
}

.gasto-pill {
  display: inline-block;
  margin-left: 0.35rem;
  padding: 0.05rem 0.4rem;
  border-radius: 999px;
  background: #e7f0ea;
  color: var(--marca-oscura, #163528);
  font-size: 0.68rem;
  font-weight: 650;
  letter-spacing: 0.01em;
  vertical-align: middle;
}

.cifra-gasto {
  color: var(--gasto, #9a3412);
  font-variant-numeric: tabular-nums;
}

.categoria-badge {
  max-width: 100%;
  font-size: 0.75rem;
  font-weight: 500;
}

/* Evita zoom al enfocar en iOS/Android y mantiene altura táctil clara */
.gasto-fecha-filtro {
  min-height: 38px;
}

.gasto-ultimo-mes-btn {
  min-height: 38px;
  white-space: nowrap;
}
@media (max-width: 767.98px) {
  .gasto-fecha-filtro {
    font-size: 16px;
  }
  .gasto-card-mobile {
    cursor: pointer;
    transition: background-color 0.15s ease;
  }
  .gasto-card-mobile:active {
    background-color: rgba(0, 0, 0, 0.04);
  }
  .gasto-cards .card {
    font-size: 0.9rem;
  }
  .pagination-nav .page-link {
    padding: 0.35rem 0.5rem;
    font-size: 0.8rem;
  }
}

/* Hover filas normales (Bootstrap pinta td, no tr) */
.gasto-list-card :deep(.table-hover tbody tr:hover > td) {
  background-color: rgba(22, 53, 40, 0.04);
  cursor: pointer;
}

.btn-group .btn {
  min-width: 80px;
}

@media (min-width: 768px) {
  .btn-group .btn {
    min-width: 100px;
  }
}

.btn-outline-secondary:hover {
  background-color: #6c757d;
  color: white;
}

.table-responsive {
  margin-bottom: 1rem;
}

.pagination {
  margin-bottom: 0;
}

.page-link {
  cursor: pointer;
}

.page-item.disabled .page-link {
  cursor: not-allowed;
}

.table th {
  cursor: pointer;
  user-select: none;
}

.table th:hover {
  background-color: rgba(0,0,0,0.05);
}

.bi {
  font-size: 0.8rem;
  margin-left: 0.25rem;
}
</style>